// src/components/scratch/PremiumLock.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "../ui/Button";
import Icon from "../ui/Icon";

/**
 * Wraps premium-only content. When `locked`, the children are blurred and a
 * call-to-action overlay invites the user to upgrade. When unlocked, children
 * render normally.
 *
 * The lock is purely presentational — the backend is still the source of truth
 * for entitlements; treat this as a UX nicety, not a security boundary.
 *
 * @param {{ locked: boolean, title?: string, blurb?: string, children: React.ReactNode }} props
 */
export default function PremiumLock({
  locked,
  title = "Unlock advanced prize insights",
  blurb = "Full prize-tier breakdowns, score history, and change alerts are part of LottoGPT Premium.",
  children,
}) {
  const navigate = useNavigate();

  if (!locked) return <>{children}</>;

  return (
    <div style={{ position: "relative" }}>
      <div
        aria-hidden="true"
        style={{ filter: "blur(6px)", pointerEvents: "none", userSelect: "none" }}
      >
        {children}
      </div>
      <div
        className="flex flex-col items-center justify-center text-center gap-3 px-6"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "var(--r-lg)",
          background: "color-mix(in srgb, var(--surf) 72%, transparent)",
        }}
      >
        <span
          className="inline-flex items-center justify-center rounded-full"
          style={{ width: 40, height: 40, background: "var(--gold-dim)", color: "var(--gold-lt)" }}
        >
          <Icon name="star-filled" size={20} />
        </span>
        <h4 className="font-semibold text-[15px]" style={{ color: "var(--txt)" }}>
          {title}
        </h4>
        <p className="text-[12px] max-w-xs" style={{ color: "var(--txt-2)" }}>
          {blurb}
        </p>
        <Button size="sm" icon="bolt" onClick={() => navigate("/plans")}>
          See plans
        </Button>
      </div>
    </div>
  );
}
